import React, { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Activity,
  ArrowLeft,
  EyeOff,
  Loader2,
  Menu,
  Pause,
  Play,
  Radar,
  Send,
  Zap,
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useChatLayout } from '../../components/layout/ChatLayout';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import {
  pulseApi,
  type PulseAccount,
  type PulseSignalsResponse,
} from '../../services/pulse-api';
import {
  outputChannelsApi,
  type OutputChannelConfig,
} from '../../services/output-channels-api';

/**
 * Pulse — Activate flow (Screen B). Three states in one page:
 *   1. Pre-analyze — "Analyze account" button
 *   2. Analyzing — spinner, polls every 2s
 *   3. Analyzed — review discovered signals, "Activate monitoring" button
 *
 * v1 only monitors the no-filter baseline; discovered fields are shown
 * read-only with a "Coming soon" affordance.
 */
const PulseActivatePage: React.FC = () => {
  const { accountId } = useParams<{ accountId: string }>();
  const { isMobile, toggleSidebar } = useChatLayout();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const accountQuery = useQuery({
    queryKey: ['pulse', 'account', accountId],
    queryFn: () => pulseApi.getAccount(accountId!),
    enabled: !!accountId,
  });

  const signalsQuery = useQuery({
    queryKey: ['pulse', 'signals', accountId],
    queryFn: () => pulseApi.getSignals(accountId!),
    enabled: !!accountId,
    refetchInterval: (query) =>
      query.state.data?.status === 'analyzing' ? 2_000 : false,
  });

  const analyzeMutation = useMutation({
    mutationFn: () => pulseApi.analyze(accountId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pulse', 'signals', accountId] });
      queryClient.invalidateQueries({ queryKey: ['pulse', 'account', accountId] });
    },
    onError: (error: unknown) => {
      toast.error(apiErrorMessage(error, 'Failed to start analysis'));
    },
  });

  const activateMutation = useMutation({
    mutationFn: (payload: {
      monitoredFields: string[];
      outputChannel: OutputChannelConfig | null;
    }) => pulseApi.activate(accountId!, payload.monitoredFields, payload.outputChannel),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pulse', 'accounts'] });
      queryClient.invalidateQueries({ queryKey: ['pulse', 'account', accountId] });
      queryClient.invalidateQueries({ queryKey: ['pulse', 'signals', accountId] });
      toast.success(
        accountQuery.data?.state === 'active' ? 'Monitoring updated' : 'Monitoring activated',
      );
      navigate(`/pulse/accounts/${accountId}`);
    },
    onError: (error: unknown) => {
      toast.error(apiErrorMessage(error, 'Failed to update monitoring'));
    },
  });

  const account = accountQuery.data;
  const signals = signalsQuery.data;
  const isLoading = accountQuery.isLoading || signalsQuery.isLoading;

  return (
    <div className="flex flex-col min-h-screen px-4 py-8 sm:py-12 relative">
      {isMobile && (
        <button
          onClick={toggleSidebar}
          className="absolute top-4 left-4 p-2 rounded-lg text-muted-foreground hover:bg-accent"
        >
          <Menu className="w-5 h-5" />
        </button>
      )}

      <div className="w-full max-w-3xl mx-auto space-y-6">
        <Link
          to="/pulse"
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="w-4 h-4" /> Back to accounts
        </Link>

        {account && <AccountHeader account={account} />}

        {isLoading && (
          <div className="flex justify-center py-16">
            <LoadingSpinner size="lg" />
          </div>
        )}

        {accountQuery.error && (
          <ErrorBanner message="Failed to load this account." />
        )}

        {!isLoading && signals && (
          <ActivateBody
            account={account}
            signals={signals}
            onAnalyze={() => analyzeMutation.mutate()}
            isAnalyzing={analyzeMutation.isPending || signals.status === 'analyzing'}
            onActivate={(payload) => activateMutation.mutate(payload)}
            isActivating={activateMutation.isPending}
          />
        )}
      </div>
    </div>
  );
};

const AccountHeader: React.FC<{ account: PulseAccount }> = ({ account }) => (
  <div className="flex items-center gap-3">
    <div className="w-10 h-10 rounded-lg bg-muted grid place-items-center">
      <Activity className="w-5 h-5 text-primary" />
    </div>
    <div>
      <h1 className="text-xl font-bold text-foreground">{account.accountName}</h1>
      <div className="text-sm text-muted-foreground">
        {account.dataSourceName} · syncs {account.syncFrequencyLabel}
      </div>
    </div>
  </div>
);

const ActivateBody: React.FC<{
  account: PulseAccount | undefined;
  signals: PulseSignalsResponse;
  onAnalyze: () => void;
  isAnalyzing: boolean;
  onActivate: (payload: {
    monitoredFields: string[];
    outputChannel: OutputChannelConfig | null;
  }) => void;
  isActivating: boolean;
}> = ({ account, signals, onAnalyze, isAnalyzing, onActivate, isActivating }) => {
  if (signals.status === 'analyzing' || isAnalyzing) {
    return <AnalyzingState />;
  }
  if (signals.status === 'failed') {
    return (
      <div className="space-y-4">
        <ErrorBanner message={signals.error ?? 'Analysis failed unexpectedly.'} />
        <button
          onClick={onAnalyze}
          className="inline-flex items-center gap-1.5 text-sm font-medium bg-primary text-white rounded-lg px-4 py-2.5 hover:bg-primary transition-colors"
        >
          <Radar className="w-4 h-4" /> Try again
        </button>
      </div>
    );
  }
  if (signals.status === 'analyzed') {
    return (
      <ReviewState
        account={account}
        signals={signals}
        onActivate={onActivate}
        isActivating={isActivating}
      />
    );
  }
  // 'not_analyzed'
  return <PreAnalyzeState onAnalyze={onAnalyze} />;
};

const PreAnalyzeState: React.FC<{ onAnalyze: () => void }> = ({ onAnalyze }) => (
  <div className="rounded-xl bg-white dark:bg-white/5 border border-border p-8 text-center">
    <div className="w-12 h-12 rounded-xl bg-accent grid place-items-center mx-auto mb-4">
      <Radar className="w-6 h-6 text-primary" />
    </div>
    <h2 className="text-lg font-semibold text-foreground">Analyze this account</h2>
    <p className="text-sm text-muted-foreground mt-2 max-w-md mx-auto">
      Pulse will scan the last 30 days of transactions to find which activity patterns are
      consistent enough to monitor reliably. This usually takes a minute or two.
    </p>
    <button
      onClick={onAnalyze}
      className="mt-5 inline-flex items-center justify-center gap-1.5 text-sm font-medium bg-primary text-white rounded-lg px-4 py-2.5 hover:bg-primary transition-colors"
    >
      <Radar className="w-4 h-4" /> Analyze account
    </button>
  </div>
);

const AnalyzingState: React.FC = () => (
  <div className="rounded-xl bg-white dark:bg-white/5 border border-border p-8 text-center">
    <div className="w-12 h-12 rounded-xl bg-accent grid place-items-center mx-auto mb-4">
      <Loader2 className="w-6 h-6 text-primary animate-spin" />
    </div>
    <h2 className="text-lg font-semibold text-foreground">Analyzing transaction patterns…</h2>
    <p className="text-sm text-muted-foreground mt-2">
      Evaluating consistent activity across this account.
    </p>
    <p className="text-xs text-muted-foreground mt-4">
      You can leave this page — we'll keep going and notify you when it's ready.
    </p>
  </div>
);

const ReviewState: React.FC<{
  account: PulseAccount | undefined;
  signals: PulseSignalsResponse;
  onActivate: (payload: {
    monitoredFields: string[];
    outputChannel: OutputChannelConfig | null;
  }) => void;
  isActivating: boolean;
}> = ({ account, signals, onActivate, isActivating }) => {
  const isActive = account?.state === 'active';
  const isPaused = account?.state === 'paused';
  const accountId = account?.accountId;
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<Set<string>>(
    () => new Set(signals.monitoredFields ?? []),
  );
  const [outputChannel, setOutputChannel] = useState<OutputChannelConfig | null>(
    () => signals.outputChannel ?? null,
  );
  const { data: channelsData } = useQuery({
    queryKey: ['pulse', 'output-channels'],
    queryFn: () => outputChannelsApi.getOutputChannels(),
  });
  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['pulse', 'accounts'] });
    queryClient.invalidateQueries({ queryKey: ['pulse', 'account', accountId] });
    queryClient.invalidateQueries({ queryKey: ['pulse', 'signals', accountId] });
  };
  const pauseMutation = useMutation({
    mutationFn: () => pulseApi.pause(accountId!),
    onSuccess: () => {
      invalidate();
      toast.success('Monitoring paused');
    },
    onError: (error: unknown) => toast.error(apiErrorMessage(error, 'Failed to pause monitoring')),
  });
  const resumeMutation = useMutation({
    mutationFn: () => pulseApi.resume(accountId!),
    onSuccess: () => {
      invalidate();
      toast.success('Monitoring resumed');
    },
    onError: (error: unknown) => toast.error(apiErrorMessage(error, 'Failed to resume monitoring')),
  });
  const isTogglingState = pauseMutation.isPending || resumeMutation.isPending;
  const toggle = (key: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  const fieldCount = signals.fields?.length ?? 0;
  const dailyAvg = signals.baseline?.dailyAvgLast7Days ?? 0;
  return (
    <div className="space-y-4">
      <div className="rounded-xl bg-white dark:bg-white/5 border border-border overflow-hidden">
        <div className="p-5 border-b border-border">
          <h2 className="font-semibold text-foreground">Review what Pulse found</h2>
          <p className="text-sm text-muted-foreground mt-1">
            We analyzed the last 30 days and found high-activity patterns across{' '}
            <span className="font-medium text-foreground">{fieldCount} field{fieldCount === 1 ? '' : 's'}</span>.
            Pulse always monitors{' '}
            <span className="font-medium text-foreground">overall account activity</span>; select any additional
            fields you'd like to monitor.
          </p>
        </div>

        <div className="p-5 border-b border-border bg-accent/40">
          <label className="flex items-start gap-3 cursor-pointer">
            <input type="checkbox" checked readOnly className="mt-0.5 w-4 h-4 rounded accent-primary" />
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <span className="font-medium text-foreground">Overall account activity</span>
                <span className="text-[11px] font-medium text-primary bg-accent px-1.5 py-0.5 rounded">
                  Monitored
                </span>
              </div>
              <div className="text-sm text-muted-foreground mt-0.5">
                Every transaction on this account · ~{Math.round(dailyAvg)} orders/day
              </div>
            </div>
            {signals.baseline && <TierPill tier={signals.baseline.tier} />}
          </label>
        </div>

        {signals.fields?.map((field) => (
          <label
            key={field.field}
            className="block p-5 border-b last:border-b-0 border-border cursor-pointer hover:bg-accent/20"
          >
            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                checked={selected.has(field.field)}
                onChange={() => toggle(field.field)}
                className="mt-0.5 w-4 h-4 rounded accent-primary cursor-pointer"
              />
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-foreground">{titleCase(field.field)}</span>
                </div>
                <div className="text-sm text-muted-foreground mt-0.5">
                  {field.values.length} high-activity value{field.values.length === 1 ? '' : 's'} found
                </div>
                <div className="flex flex-wrap gap-1.5 mt-2.5">
                  {field.values.map((v) => (
                    <span
                      key={v.value}
                      className="text-[11px] font-medium text-muted-foreground bg-muted px-2 py-1 rounded"
                    >
                      {v.displayName ?? v.value} · {v.tier}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </label>
        ))}
      </div>

      <div className="flex items-start gap-2 text-xs text-muted-foreground bg-muted rounded-lg px-3 py-2.5">
        <EyeOff className="w-3.5 h-3.5 mt-0.5 shrink-0" />
        <span>
          Values without enough consistent activity were left out automatically (Medium tier or
          lower).
        </span>
      </div>

      <div className="rounded-xl bg-white dark:bg-white/5 border border-border p-5">
        <div className="flex items-center gap-2">
          <Send className="w-4 h-4 text-muted-foreground" />
          <span className="text-sm font-semibold text-foreground">Alert delivery</span>
        </div>
        <p className="text-xs text-muted-foreground mt-1 mb-3">
          Where Pulse sends alerts. Results are always saved as in-app chat threads.
        </p>
        <select
          value={outputChannel ? `${outputChannel.source}:${outputChannel.id}` : 'chat'}
          onChange={(e) => {
            if (e.target.value === 'chat') {
              setOutputChannel(null);
            } else {
              const [source, id] = e.target.value.split(':') as [
                OutputChannelConfig['source'],
                string,
              ];
              setOutputChannel({ source, id });
            }
          }}
          className="w-full px-3 py-2 rounded-lg border border-border bg-card text-sm text-foreground"
        >
          <option value="chat">In-app only</option>
          {channelsData?.channels.map((ch) => (
            <option key={`${ch.source}:${ch.id}`} value={`${ch.source}:${ch.id}`}>
              {ch.label}
              {ch.detail ? ` — ${ch.detail}` : ''} ({ch.type.toUpperCase()})
            </option>
          ))}
        </select>
      </div>

      <div className="flex items-center justify-between bg-white dark:bg-white/5 border border-border rounded-xl px-5 py-4">
        <div className="text-sm">
          <span className="font-semibold text-foreground">Overall account activity</span>
          {selected.size > 0 && (
            <span className="font-semibold text-foreground">
              {' '}+ {selected.size} field{selected.size === 1 ? '' : 's'}
            </span>
          )}{' '}
          <span className="text-muted-foreground">will be monitored</span>
          <span className="text-muted-foreground"> · </span>
          <span className="text-muted-foreground">
            checks {deriveExecutionLabel(account?.syncFrequency)} · refreshes daily
          </span>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {isActive && (
            <button
              onClick={() => pauseMutation.mutate()}
              disabled={isTogglingState || isActivating}
              className="inline-flex items-center gap-1.5 text-sm font-medium border border-input dark:border-white/10 rounded-lg px-3 py-2.5 hover:bg-muted dark:hover:bg-white/10 transition-colors disabled:opacity-50"
            >
              {pauseMutation.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Pause className="w-4 h-4" />
              )}
              Pause
            </button>
          )}
          {isPaused && (
            <button
              onClick={() => resumeMutation.mutate()}
              disabled={isTogglingState || isActivating}
              className="inline-flex items-center gap-1.5 text-sm font-medium border border-input dark:border-white/10 rounded-lg px-3 py-2.5 hover:bg-muted dark:hover:bg-white/10 transition-colors disabled:opacity-50"
            >
              {resumeMutation.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Play className="w-4 h-4" />
              )}
              Resume
            </button>
          )}
          <button
            onClick={() => onActivate({ monitoredFields: [...selected], outputChannel })}
            disabled={isActivating}
            className="inline-flex items-center gap-1.5 text-sm font-medium bg-primary text-white rounded-lg px-4 py-2.5 hover:bg-primary transition-colors disabled:opacity-50"
          >
            {isActivating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
            {isActive ? 'Update monitoring' : isPaused ? 'Update & resume' : 'Activate monitoring'}
          </button>
        </div>
      </div>
    </div>
  );
};

const TierPill: React.FC<{ tier: string }> = ({ tier }) => (
  <span className="text-[11px] font-semibold text-primary bg-accent px-2 py-0.5 rounded-full whitespace-nowrap">
    {tier} activity
  </span>
);

const ErrorBanner: React.FC<{ message: string }> = ({ message }) => (
  <div className="rounded-xl bg-destructive/15 border border-destructive/20 text-destructive p-4">
    {message}
  </div>
);

function titleCase(s: string): string {
  if (!s) return '';
  return s.replace(/_/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase());
}

function apiErrorMessage(error: unknown, fallback: string): string {
  const data = (error as { response?: { data?: { error?: string } } }).response?.data;
  return data?.error ?? fallback;
}

function deriveExecutionLabel(syncFrequency: string | null | undefined): string {
  switch (syncFrequency) {
    case '*/15 * * * *':
      return 'every 30 min';
    case '*/30 * * * *':
      return 'every hour';
    case '0 * * * *':
      return 'every 2 hours';
    case '0 */2 * * *':
      return 'every 4 hours';
    default:
      return 'automatically';
  }
}

export default PulseActivatePage;
